import React from 'react';
import { Box, Grid, Paper, Typography, Chip } from '@mui/material';
import {
  ACTIVE_POLICY_PACKS,
  PACK_BY_KEY,
  type PackKey,
} from '../../domain/policies/policyPacks';

export interface PackChooserStepProps {
  selectedPackKey: PackKey | null;
  onSelectPack: (packKey: PackKey) => void;
}

const PackChooserStep: React.FC<PackChooserStepProps> = ({ selectedPackKey, onSelectPack }) => {
  const workerProtectionFuturePolicies = PACK_BY_KEY.worker_protection.policies.filter((p) => p.disabled);

  return (
    <>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Choose a safety pack
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each pack groups related protections for a specific safety outcome area.
      </Typography>

      <Grid container spacing={2}>
        {ACTIVE_POLICY_PACKS.map((pack) => {
          const selected = selectedPackKey === pack.key;
          const enabledCount = pack.policies.filter((p) => !p.disabled && p.policyType).length;
          return (
            <Grid item xs={12} sm={6} key={pack.key}>
              <Paper
                onClick={() => onSelectPack(pack.key)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: selected ? 'primary.main' : 'divider',
                  bgcolor: selected ? 'primary.50' : 'background.paper',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:hover': { borderColor: selected ? 'primary.main' : 'action.hover' },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {pack.label}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {pack.key === 'worker_protection' && <Chip label="Staged" color="warning" size="small" variant="outlined" />}
                    {selected && <Chip label="Selected" color="primary" size="small" />}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {pack.valueProp}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                  Best for: {pack.bestFor}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                  {pack.cameraHint}
                </Typography>
                <Box sx={{ mt: 'auto', pt: 1 }}>
                  <Chip label={`${enabledCount} rule${enabledCount === 1 ? '' : 's'}`} size="small" variant="outlined" />
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {workerProtectionFuturePolicies.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, borderStyle: 'dashed', mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Future additions to {PACK_BY_KEY.worker_protection.label}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Worker Down is staged first. Additional Worker Protection policies stay hidden until their runtime and validation gates are ready.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {workerProtectionFuturePolicies.map((p) => (
              <Chip key={p.alias} label={p.label} size="small" variant="outlined" />
            ))}
          </Box>
        </Paper>
      )}
    </>
  );
};

export default PackChooserStep;
